import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentAccount, useCurrentClient, useCurrentNetwork } from '@mysten/dapp-kit-react'

import { COIN_INFOS } from '@kunalabs-io/kai'
import { Amount } from '@kunalabs-io/kai'
import { compressSuiType } from '../gen/_framework/util'

export interface WalletBalanceInfos {
  infos?: Map<string, Amount>
  isLoading: boolean
  triggerUpdate: () => void
}

export const useWalletBalances = (
  options?: { refetchInterval: number },
  walletAddress?: string
): WalletBalanceInfos => {
  const client = useCurrentClient()
  const currentAccount = useCurrentAccount()
  const network = useCurrentNetwork()

  const addressToUse = walletAddress || currentAccount?.address

  const balancesRes = useQuery({
    queryKey: ['wallet-balances', addressToUse, network],
    enabled: !!addressToUse,
    queryFn: async () => {
      if (!addressToUse) {
        throw new Error('invariant violation')
      }

      // listBalances is paginated; loop to preserve the completeness getAllBalances had.
      const balances = new Map<string, bigint>()
      let cursor: string | null = null
      do {
        const res = await client.core.listBalances({ owner: addressToUse, cursor })
        for (const balance of res.balances) {
          balances.set(compressSuiType(balance.coinType), BigInt(balance.balance))
        }
        cursor = res.hasNextPage ? res.cursor : null
      } while (cursor)
      return balances
    },
    refetchInterval: options?.refetchInterval,
  })
  const balances = balancesRes.data

  const isLoading = balancesRes.isLoading

  const decimals = COIN_INFOS.reduce((acc, info) => {
    acc.set(info.typeName, info.decimals)
    return acc
  }, new Map<string, number>())

  const infos = new Map<string, Amount>()
  for (const [type, d] of decimals.entries()) {
    const amount = balances?.get(type) || 0n
    infos.set(type, Amount.fromInt(amount, d))
  }

  const queryClient = useQueryClient()
  const triggerUpdate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['wallet-balances'],
    })
  }

  return {
    infos: isLoading ? undefined : infos,
    isLoading,
    triggerUpdate,
  }
}

export async function refetchWalletBalances(
  queryClient: QueryClient,
  walletAddress: string | undefined,
  invalidateOnly = false
) {
  if (!walletAddress) {
    return
  }

  if (invalidateOnly) {
    await queryClient.invalidateQueries({
      queryKey: ['wallet-balances', walletAddress],
    })
  } else {
    await queryClient.refetchQueries({
      queryKey: ['wallet-balances', walletAddress],
    })
  }
}
